import React from "react";
import { Logo } from "./Logo";
import {
  Plus, MessageSquare, Trash2, Database, Sparkles, Cpu, LineChart,
  Wallet, Users, ShoppingCart, Package, Code2, Bot, Search, Plug, X, LifeBuoy,
} from "lucide-react";

const MODULES = [
  { id: "Universal AI", icon: Sparkles },
  { id: "Data Analyst", icon: Database },
  { id: "MIS & BI", icon: LineChart },
  { id: "Finance & P&L", icon: Wallet },
  { id: "HR & Talent", icon: Users },
  { id: "Sales & Marketing", icon: ShoppingCart },
  { id: "Inventory", icon: Package },
  { id: "Developer AI", icon: Code2 },
  { id: "Forecasting", icon: Cpu },
];

export default function Sidebar({
  open, onClose, sessions, activeId, onSelect, onNew, onDelete,
  config, usage, dataset, onOpenData, onOpenResearch, onOpenConnectors, onOpenSelfAI,
  activeModule, onModule,
}) {
  const tier = usage?.tier || "Free";
  const freeWin = usage?.freeWindow;
  return (
    <>
      {open && <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={onClose} />}
      <aside
        data-testid="sidebar"
        className={`fixed lg:static z-50 top-0 left-0 h-full w-72 shrink-0 bg-[#0B0F17] border-r border-white/10 flex flex-col transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        <div className="flex items-center justify-between px-4 py-4 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <Logo size={36} className="shadow-lg shadow-blue-500/10" />
            <div>
              <div className="font-head font-bold text-white leading-none tracking-tight">SSG AI</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">Bharat Intelligence</div>
            </div>
          </div>
          <button className="lg:hidden text-slate-400" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="p-3">
          <button
            data-testid="new-chat-button"
            onClick={onNew}
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-sm transition-colors shadow-lg shadow-emerald-500/20"
          >
            <Plus size={16} /> New chat
          </button>
        </div>

        <div className="px-3 pb-2">
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 px-1 mb-1.5">Modules</div>
          <div className="grid grid-cols-3 gap-1.5">
            {MODULES.map((m) => {
              const Icon = m.icon;
              const active = activeModule === m.id;
              return (
                <button
                  key={m.id}
                  data-testid={`module-${m.id.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => onModule(m.id)}
                  title={m.id}
                  className={`flex flex-col items-center gap-1 py-2 rounded-lg border text-[10px] leading-tight text-center transition-colors ${active ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "bg-white/5 border-white/10 text-slate-400 hover:text-white hover:bg-white/10"}`}
                >
                  <Icon size={15} />
                  <span className="line-clamp-1">{m.id.split(" ")[0]}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="px-3 py-2 flex gap-2">
          <button data-testid="open-research-button" onClick={onOpenResearch} className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-300 hover:bg-white/10">
            <Search size={14} /> Research
          </button>
          <button data-testid="open-connectors-button" onClick={onOpenConnectors} className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-300 hover:bg-white/10">
            <Plug size={14} /> Apps
          </button>
        </div>

        <div className="px-3 pb-1">
          <button data-testid="sidebar-selfai-button" onClick={onOpenSelfAI} className="w-full flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-300 hover:bg-white/10 hover:text-emerald-300">
            <LifeBuoy size={14} /> SSG AI Self AI · Help
          </button>
        </div>

        {dataset && (
          <div className="px-3 pb-2">
            <button data-testid="open-datastudio-button" onClick={onOpenData} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500/15 to-blue-500/10 border border-emerald-500/30 text-sm text-emerald-200 hover:border-emerald-400/60 transition">
              <Database size={15} />
              <span className="truncate flex-1 text-left">{dataset.filename}</span>
              <span className="text-[10px] text-slate-400">{dataset.analysis?.rowCount} rows</span>
            </button>
          </div>
        )}

        <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 px-4 pt-1 pb-1">Recent</div>
        <div className="flex-1 overflow-y-auto px-2 space-y-0.5">
          {sessions.length === 0 && <div className="text-xs text-slate-600 px-3 py-4">No conversations yet.</div>}
          {sessions.map((s) => (
            <div
              key={s.id}
              data-testid="session-item"
              onClick={() => onSelect(s.id)}
              className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-sm transition-colors ${activeId === s.id ? "bg-white/10 text-white" : "text-slate-400 hover:bg-white/5 hover:text-slate-200"}`}
            >
              <MessageSquare size={14} className="shrink-0 opacity-70" />
              <span className="truncate flex-1">{s.title}</span>
              <button
                data-testid="delete-session-button"
                onClick={(e) => { e.stopPropagation(); onDelete(s.id); }}
                className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 transition"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10 p-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-slate-400">Plan</span>
            <span className="text-xs font-semibold text-gold" style={{ color: "#F59E0B" }}>{tier}</span>
          </div>
          {tier === "Free" && freeWin && (
            <div className="mb-2" data-testid="free-quota-indicator">
              <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
                <span>Free questions (24h)</span><span>{freeWin.remaining}/{freeWin.limit} left</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full rounded-full bg-emerald-500" style={{ width: `${(freeWin.remaining / freeWin.limit) * 100}%` }} />
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Bot size={14} className={config?.ai?.configured ? "text-emerald-400" : "text-slate-500"} />
            <span className="text-[11px] text-slate-500">
              {config?.ai?.configured ? "AI connected" : "AI not configured"}
            </span>
          </div>
        </div>
      </aside>
    </>
  );
}
